class ExponeaReco extends HTMLElement {
  static SESSION_STORAGE_KEY = 'exponeaCachedHandles';
  static OBSERVER_ROOT_MARGIN = '150px';
  static TIMEOUT_DURATION = 2000; // in ms
  static DEFAULT_PRODUCTS_TO_SHOW = 10;
  static SPARE_HANDLES = 5;
  static WISELLI_VARIANT_TIMEOUT = 1000; // in ms

  constructor() {
    super();
    this.status = 'PENDING'; // PENDING, LOADED, TIMED_OUT
    this.abTestVariant = false;
    this._loadStarted = false;
  }

  connectedCallback() {
    this.productsContainer = this.querySelector('.js-container-for-products');
    this.productsToShow = Number(this.getAttribute('products-to-show')) || ExponeaReco.DEFAULT_PRODUCTS_TO_SHOW;
    this.recommendationId = this.getAttribute('recommendation-id');
    this.recommendationAbId = this.getAttribute('recommendation-ab-id');
    this.recommendationAbVariantWeight = this.getAttribute('recommendation-ab-variant-weight');
    this.recommendationContextSku = this.getAttribute('recommendation-context-sku') || '';
    this.cachedHandles = [];
    this.fallbackHandles = (this.getAttribute('fallback-handles') || '')
      .split('+')
      .map((handle) => handle.trim())
      .filter(Boolean);
    this.excludedHandles = (this.getAttribute('excluded-handles') || '')
      .split('+')
      .map((handle) => handle.trim())
      .filter(Boolean);
    this.placement = this.getAttribute('recommendation-placement') || '';

    this.updateCachedHandles();
    if (!this._loadStarted) {
      this._preloadObserver.observe(this);
    }
    this.productsContainer.addEventListener('click', this._handleRecommendationsClick);
  }

  /**
   * The single entry point to a load, so no caller can start a second one on the
   * same element. A repeat load spends another Exponea round-trip and swaps the
   * row out from under whoever is already looking at it.
   */
  _startLoad() {
    if (this._loadStarted) return;
    this._loadStarted = true;
    this.classList.add('is-observed');
    this._preloadObserver.disconnect();
    this._load();
  }

  /**
   * Force-load when the host was mounted while hidden (e.g. search idle panel).
   * IntersectionObserver often never fires in that case, so we only ever hit fallback.
   */
  ensureLoaded() {
    this._startLoad();
  }

  disconnectedCallback() {
    if (this._timeout) {
      clearTimeout(this._timeout);
    }
    if (this._wiselliTimeout) {
      clearTimeout(this._wiselliTimeout);
    }
    if (this._preloadObserver) {
      this._preloadObserver.disconnect();
    }
    this.productsContainer.removeEventListener('click', this._handleRecommendationsClick);
  }

  updateCachedHandles() {
    const sessionData = sessionStorage.getItem(ExponeaReco.SESSION_STORAGE_KEY);
    if (sessionData) {
      this.cachedHandles = sessionData.split('+').map((item) => encodeURIComponent(item));
    }
  }

  _preloadObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        this._startLoad();
      }
    },
    { rootMargin: ExponeaReco.OBSERVER_ROOT_MARGIN },
  );

  // main component logic
  async _load() {
    try {
      if (window.isExponeaDisabled) {
        if (this._keepExistingProducts()) return;
        this._fetchAndRenderProducts([...this.cachedHandles, ...this.fallbackHandles]);
        return;
      }

      // check if there wiselli settings stored in localStorage for this reco
      const shopifySection = this.closest('[id^="shopify-section"]');
      if (shopifySection) {
        const wiselliKey = `${shopifySection.id}_reco`;
        const wiselliRecoSettings = JSON.parse(localStorage.getItem(wiselliKey));
        if (wiselliRecoSettings) {
          const wiselliRecoId = wiselliRecoSettings['recommendation-id'];
          const wiselliVariant = wiselliRecoSettings['wiselli-variant'];
          const expirationTimestamp = wiselliRecoSettings['expirationTimestamp'];
          const dateNow = Date.now();

          if (wiselliVariant && expirationTimestamp && dateNow < expirationTimestamp) {
            this.abTestVariant = 'WiselliTarget';
            if (wiselliRecoId) {
              this.setAttribute('recommendation-id', wiselliRecoId);
              this.recommendationId = wiselliRecoId;
            }
            this.setAttribute('wiselli-variant', wiselliVariant);
            this.abTestVariantWiselli = wiselliVariant;
            this._loadRecommendations();
            return;
          }
        }
      }
      this.abTestVariant = await this._getAbTestVariant();

      if (this.abTestVariant === 'WiselliTarget') {
        // static timeout to make sure Wiselli script updated the reco ID
        this._wiselliTimeout = setTimeout(() => {
          this.recommendationId = this.getAttribute('recommendation-id');
          this.abTestVariantWiselli = this.getAttribute('wiselli-variant') || 'wiselli-timeout';
          this._loadRecommendations();
        }, ExponeaReco.WISELLI_VARIANT_TIMEOUT);
      } else {
        this._loadRecommendations();
      }
    } catch (error) {
      if (this._keepExistingProducts()) return;
      this._fetchAndRenderProducts(this.fallbackHandles);
    }
  }

  // A/B test handling exponea split for Wiselli test, if configured for reco component
  _getAbTestVariant() {
    if (this.recommendationAbId === 'WiselliGlobalStandalone') {
      const testName = this.recommendationAbId;
      const variantWeight = {
        WiselliTarget: Number(this.recommendationAbVariantWeight),
        WiselliControl: 100 - Number(this.recommendationAbVariantWeight),
      };

      return new Promise((resolve) => {
        window.exponea.getAbTest(testName, variantWeight, (variant) => {
          resolve(variant);
        });
      });
    }
    return Promise.resolve(false);
  }

  _loadRecommendations() {
    if (!this.isConnected) return;

    const hasGetRecommendation = Boolean(window.exponea && typeof window.exponea.getRecommendation === 'function');
    const options = {
      recommendationId: this.recommendationId,
      size: this.productsToShow,
      callback: this._handleRecommendationsLoaded.bind(this),
      fillWithRandom: true,
      catalogFilter: [],
      catalogAttributesWhitelist: ['HANDLE', 'ITEM_GROUP_ID'],
      items: {},
    };
    if (this.recommendationContextSku) {
      options.items[this.recommendationContextSku] = 1;
    }

    if (hasGetRecommendation) {
      window.exponea.getRecommendation(options);
    } else {
      // No SDK callback will arrive — resolve immediately instead of waiting on timeout.
      this.status = 'TIMED_OUT';
      this._fetchAndRenderProducts([...this.cachedHandles, ...this.fallbackHandles]);
      return;
    }

    this._timeout = setTimeout(() => {
      if (this.status === 'LOADED') return;
      this.status = 'TIMED_OUT';
      this._trackRecommendation({ action: 'timeout' });
      if (this._keepExistingProducts()) return;
      this._fetchAndRenderProducts([...this.cachedHandles, ...this.fallbackHandles]);
    }, ExponeaReco.TIMEOUT_DURATION);
  }

  _hasRenderableProducts() {
    if (!this.productsContainer) return false;
    return Boolean(
      this.productsContainer.querySelector(
        '.product-card-wrapper, .card-wrapper, [data-product-handle], a[href*="/products/"]',
      ),
    );
  }

  _getProductCount() {
    if (!this.productsContainer) return 0;

    const fetchedItems = this.productsContainer.querySelectorAll('.js-fetched-item');
    if (fetchedItems.length) return fetchedItems.length;

    if (!this._hasRenderableProducts()) return 0;

    const cardRoots = this.productsContainer.querySelectorAll(
      '.js-product-card-wrapper, .product-card-wrapper, .card-wrapper, [data-product-handle]',
    );
    if (cardRoots.length) return cardRoots.length;

    return this.productsContainer.querySelectorAll('a[href*="/products/"]').length;
  }

  _dispatchLoadedEvent() {
    this.dispatchEvent(
      new CustomEvent('exponea-reco:loaded', {
        bubbles: true,
        detail: {
          count: this._getProductCount(),
          recommendationId: this.recommendationId,
        },
      }),
    );
  }

  _keepExistingProducts() {
    if (!this._hasRenderableProducts()) return false;
    this.productsContainer.querySelectorAll('li[aria-hidden="true"]').forEach((el) => el.remove());
    this._dispatchLoadedEvent();
    return true;
  }

  _clearIfEmpty() {
    if (this._hasRenderableProducts()) return;
    if (!this.productsContainer) return;
    this.productsContainer.innerHTML = '';
    this._dispatchLoadedEvent();
    this._refreshCarousel();
  }

  _handleRecommendationsLoaded(data) {
    // The SDK can call back more than once, late after a timeout, and after a
    // cart re-render has already discarded this element.
    if (!this.isConnected) return;
    if (this.status !== 'PENDING') return;
    this.status = 'LOADED';
    clearTimeout(this._timeout);

    let options = {};
    if (!data || data.length === 0) {
      options = {
        action: 'show',
        item_ids: [],
        note: 'no recommendations provided, fallback shown',
      };
      this._trackRecommendation(options);

      // Prefer already-SSR'd manual favorites over another fetch round-trip.
      if (this._keepExistingProducts()) return;

      this._fetchAndRenderProducts([...this.cachedHandles, ...this.fallbackHandles]);
    } else {
      const recommendedHandles = data
        .map((item) => item?.HANDLE)
        .filter(Boolean)
        .map((handle) => encodeURIComponent(handle));
      options = {
        action: 'show',
        item_ids: data.map((item) => item.ITEM_GROUP_ID),
      };

      this._fetchAndRenderProducts([...recommendedHandles, ...this.cachedHandles, ...this.fallbackHandles]);
      this._trackRecommendation(options);
    }
  }

  async _fetchAndRenderProducts(handles) {
    if (!this.isConnected) return;
    if (!handles || handles.length === 0) {
      if (!this._keepExistingProducts()) this._clearIfEmpty();
      return;
    }
    if (!this.productsContainer) return;

    try {
      const uniqueHandlesSet = new Set(handles);
      this.excludedHandles.forEach((handle) => uniqueHandlesSet.delete(handle));
      handles = [...uniqueHandlesSet].slice(0, this.productsToShow + ExponeaReco.SPARE_HANDLES);
      const campaign_code = new URLSearchParams(window.location.search).get('code');
      const baseUrl = window.routes.all_products_collection_url || '/collections/all';
      const url =
        `${baseUrl}?view=list-products&limit=${this.productsToShow}&type=${handles.join('+')}` +
        (campaign_code ? `&q=${campaign_code}` : '');
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);

      this._renderHtml(await response.text());
      this._refreshCarousel();
    } catch (error) {
      if (!this._keepExistingProducts()) this._clearIfEmpty();
    }
  }

  _renderHtml(htmlText) {
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(htmlText, 'text/html');
    const parsedElements = parsedHTML.querySelectorAll('.js-fetched-item');
    const fragment = document.createDocumentFragment();
    const useCarousel = Boolean(this.productsContainer.closest('.product-carousel'));
    const extraItemClasses = (this.getAttribute('product-item-class') || '').split(/\s+/).filter(Boolean);

    parsedElements.forEach((element, index) => {
      if (useCarousel) {
        element.setAttribute('id', `Slide-prlist-${index + 1}`);
        element.classList.add('product-carousel__slide');
      }
      if (extraItemClasses.length) {
        element.classList.add(...extraItemClasses);
      }
      fragment.appendChild(element);
    });
    this.productsContainer.innerHTML = '';
    window.updateWishlistStateImmediately?.(fragment);
    this.productsContainer.appendChild(fragment);
    this._dispatchLoadedEvent();
  }

  _handleRecommendationsClick = (event) => {
    const anchorElement = event.target.closest('a');
    if (anchorElement) {
      this._trackRecommendation({ action: 'click' });
    }
  };

  _refreshCarousel() {
    this.productsContainer.closest('.product-carousel')?.refresh?.();
  }

  _trackRecommendation(properties = {}) {
    if (this.abTestVariant) {
      properties.variant = this.abTestVariant;
    }
    if (this.abTestVariantWiselli) {
      properties.variant_wiselli = this.abTestVariantWiselli;
    }

    if (typeof exponeaTrackSafe === 'function')
      exponeaTrackSafe('recommendation', {
        recommendation_id: this.recommendationId,
        placement: this.placement,
        ...properties,
      });
  }
}

customElements.define('exponea-reco', ExponeaReco);

function handleGlobalCachedRecommendations(data) {
  const SESSION_STORAGE_KEY = 'exponeaCachedHandles';
  const recommendationId = window.themeSettings?.exponea_reco_id_for_cache;
  if (data && data.length > 0) {
    const handles = data.map((item) => item.HANDLE);
    sessionStorage.setItem(SESSION_STORAGE_KEY, handles.join('+'));
    if (typeof exponeaTrackSafe === 'function') {
      exponeaTrackSafe('recommendation', {
        action: 'cached',
        recommendation_id: recommendationId,
      });
    }
    document.querySelectorAll('exponea-reco').forEach((component) => {
      if (typeof component.updateCachedHandles === 'function') {
        component.updateCachedHandles();
      }
    });
  }
}

function initExponeaCache() {
  if (sessionStorage.getItem('exponeaCachedHandles')) {
    return;
  }
  const recommendationId = window.themeSettings?.exponea_reco_id_for_cache;
  const productsQty = parseInt(window.themeSettings?.exponea_reco_cached_qty) || 10;

  if (window.exponea && typeof window.exponea.getRecommendation === 'function' && recommendationId) {
    const options = {
      recommendationId: recommendationId,
      size: productsQty,
      callback: handleGlobalCachedRecommendations,
      fillWithRandom: true,
      catalogAttributesWhitelist: ['HANDLE'],
    };

    window.exponea.getRecommendation(options);
  }
}

initExponeaCache();
